import React, { useEffect } from 'react';
import useDismissOnScroll from 'hooks/useDismissOnScroll';
import {
  Button,
  ButtonType,
  Checkbox,
  Tooltip
} from 'syngenta-digital-cropwise-react-ui-kit';
import UndoIcon from 'assets/undo.svg?react';
import InfoIcon from 'assets/info.svg?react';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';

interface Props {
  /** The Price per Unit field, rendered to the left of the reset button. */
  children: React.ReactNode;
  isResetDisabled: boolean;
  onReset: () => void;
  isCheckboxDisabled: boolean;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const ListPriceControls: React.FC<Props> = ({
  children,
  isResetDisabled,
  onReset,
  isCheckboxDisabled,
  checked,
  onCheckedChange
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useDismissOnScroll();

  // Resetting unmounts the Tooltip while it is open, and the open state outlives
  // it — without this the next enable re-mounts it already showing, unhovered.
  useEffect(() => {
    if (isResetDisabled) setIsTooltipOpen(false);
  }, [isResetDisabled, setIsTooltipOpen]);

  const resetButton = (
    <Button
      aria-label="Reset to saved list price"
      data-testid="fill-product-reset-list-price"
      disabled={isResetDisabled}
      ghost={!isResetDisabled}
      type={isResetDisabled ? ButtonType.link : ButtonType.primary}
      icon={<UndoIcon />}
      onClick={onReset}
      className="p-1px ml-2"
      style={{
        width: '32px',
        height: '32px',
        color: isResetDisabled
          ? 'var(--color-neutral-30)'
          : 'var(--color-green-60)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: isResetDisabled
          ? 'var(--color-neutral-20)'
          : 'var(--color-green-60)',
        ...(isResetDisabled
          ? { backgroundColor: 'var(--color-neutral-10)' }
          : {})
      }}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end">
        <div className="flex-1 min-w-0">{children}</div>
        {isResetDisabled ? (
          resetButton
        ) : (
          <Tooltip
            title="Reset to saved list price"
            placement="topRight"
            // Driven explicitly rather than left to the default hover handling:
            // inside a scrollable drawer the popup must also close on scroll,
            // which `mouseleave` alone does not guarantee.
            open={isTooltipOpen}
            onOpenChange={setIsTooltipOpen}
            overlayClassName="cwf-list-price-tooltip"
            getPopupContainer={getDropdownParentWithoutDetaching}
          >
            {resetButton}
          </Tooltip>
        )}
      </div>

      <Checkbox
        data-testid="fill-product-save-as-list-price"
        disabled={isCheckboxDisabled}
        checked={checked}
        onChange={e => onCheckedChange(e.target.checked)}
      >
        Save as new list price
      </Checkbox>

      {checked && (
        <div
          className="flex items-center"
          style={{ gap: '4px' }}
          data-testid="fill-product-list-price-info"
        >
          <InfoIcon
            className="cwf-list-price-info-icon"
            style={{ flexShrink: 0, width: 18, height: 18 }}
          />
          <span
            className="text-sm font-normal leading-5 text-neutral-90"
            style={{ letterSpacing: '-0.105px' }}
          >
            This will not change any existing records.
          </span>
        </div>
      )}
    </div>
  );
};

export default ListPriceControls;
